import { Observable, catchError, map, of } from 'rxjs';
import { ok, fail, IResult } from 'typescript-monads';
import { HttpClientInterface } from '@itk/http-client-interface';
import {
  CrmEntry,
  PushLeadToCrmRequest,
  PushLeadToCrmResponse,
  SaveClientQuote,
} from '../models/crm.model';

export class CrmEndpoint {
  constructor(
    private readonly http: HttpClientInterface,
    private readonly crmV2Context: boolean,
  ) {}

  pushLeadToCrm(req: PushLeadToCrmRequest): Observable<PushLeadToCrmResponse> {
    if (!this.crmV2Context) {
      /**
       * This route isn't authenticated by the normal JWT token, so we don't want to pass along
       * any kind of Authorization header. For that reason we'll override the headers here.
       */
      return this.http.post<PushLeadToCrmResponse, PushLeadToCrmRequest>({
        url: '/crm/lead/',
        body: req,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    throw new Error('pushLeadToCrm is not implemented for the CRM v2 context.');
  }

  pushClientToCrm(req: CrmEntry): Observable<PushLeadToCrmResponse> {
    if (!this.crmV2Context) {
      /**
       * This route isn't authenticated by the normal JWT token, so we don't want to pass along
       * any kind of Authorization header. For that reason we'll override the headers here.
       */
      return this.http.post<PushLeadToCrmResponse, CrmEntry>({
        url: '/crm/client/',
        body: req,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    throw new Error(
      'pushClientToCrm is not implemented for the CRM v2 context.',
    );
  }

  saveClientQuote(
    clientId: string,
    req: SaveClientQuote,
  ): Observable<IResult<SaveClientQuote[], string>> {
    if (!this.crmV2Context) {
      throw new Error(
        'saveClientQuote is not implemented for the CRM v1 context.',
      );
    }

    return this.http
      .post<SaveClientQuote[], SaveClientQuote>({
        url: `/crm/client-quotes/${clientId}/`,
        body: req,
      })
      .pipe(
        map((x) => ok<SaveClientQuote[], string>(x)),
        catchError((_: unknown) =>
          of(fail<SaveClientQuote[], string>('Failed to save quote.')),
        ),
      );
  }

  getClientQuotes(
    clientId: string,
  ): Observable<IResult<SaveClientQuote[], string>> {
    if (!this.crmV2Context) {
      throw new Error(
        'getClientQuotes is not implemented for the CRM v1 context.',
      );
    }

    return this.http
      .get<SaveClientQuote[]>({
        url: `/crm/client-quotes/${clientId}/`,
      })
      .pipe(
        map((x) => ok<SaveClientQuote[], string>(x)),
        catchError((_: unknown) =>
          of(fail<SaveClientQuote[], string>('Failed to fetch quotes.')),
        ),
      );
  }

  loadClientQuote(
    clientId: string,
    quoteId: string,
  ): Observable<IResult<SaveClientQuote, string>> {
    if (!this.crmV2Context) {
      throw new Error(
        'getClientQuote is not implemented for the CRM v1 context.',
      );
    }

    return this.http
      .get<Record<string, unknown>>({
        url: `/crm/client-quotes/${clientId}/${quoteId}/`,
      })
      .pipe(
        map((x) => ok<Record<string, unknown>, string>(x)),
        catchError((_: unknown) =>
          of(fail<Record<string, unknown>, string>('Failed to fetch quote.')),
        ),
      );
  }

  removeClientQuote(
    clientId: string,
    quoteId: string,
  ): Observable<IResult<SaveClientQuote[], string>> {
    if (!this.crmV2Context) {
      throw new Error(
        'removeClientQuote is not implemented for the CRM v1 context.',
      );
    }

    return this.http
      .delete<SaveClientQuote[], unknown>({
        url: `/crm/client-quotes/${clientId}/${quoteId}/`,
        body: {},
      })
      .pipe(
        map((x) => ok<SaveClientQuote[], string>(x)),
        catchError((_: unknown) =>
          of(fail<SaveClientQuote[], string>('Failed to remove quote.')),
        ),
      );
  }
}
